import React from 'react';
import { useModalProps } from '@westwing/ui-kit/Modal';
import * as Styled from './ProjectSlider.styles';
import Carousel from './Carousel';
import { ZoomImage } from './ZoomImage';
import Divider from '@ids/components/Layout/Divider';
import Center from '@ids/components/Layout/Center';
import CTAButton, { useCTAButtonTracking } from '@ids/components/CTAButton';
import { useProjectSliderBlock } from '@ids/context';
import Media from '@ids/components/Media';
import { IDSContentfulImageFile, ProjectSlide } from '@ids/types/contentful';
import useOneOffTracking from '@ids/hooks/useOneOffTracking';
import Skeleton from '@ids/components/Layout/Skeleton';

interface SlideProps extends ProjectSlide {
    allImages: IDSContentfulImageFile[];
    index: number;
}

function Slide({ title, contentText, image, allImages, index }: SlideProps) {
    const modalProps = useModalProps();
    const [currentIndex, setCurrentIndex] = React.useState(0);

    const onOpenOverlay = () => {
        modalProps.onOpen();
        setCurrentIndex(index);
    };

    const showPrevSlide = () => setCurrentIndex(prevIndex => Math.max(prevIndex - 1, 0));
    const showNextSlide = () => setCurrentIndex(prevIndex => Math.min(prevIndex + 1, allImages.length - 1));

    const onKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
        if (e.key === 'ArrowRight') {
            showNextSlide();
        }

        if (e.key === 'ArrowLeft') {
            showPrevSlide();
        }
    };

    return (
        <Styled.SlideContainer data-testid="project-slider-slide">
            <Styled.SlideImage onClick={onOpenOverlay}>
                <Media.Image alt={title} image={image} layout="fill" sizes="(max-width: 1440px) 100vw, 33vw" />
            </Styled.SlideImage>
            <Styled.SlideTitle>{title}</Styled.SlideTitle>
            <Styled.SlideText>{contentText}</Styled.SlideText>
            <Styled.ModalOverlay {...modalProps}>
                <Styled.ModalContent $imageWidth={image.file.details.image.width} onKeyDown={onKeyDown}>
                    <Styled.LeftArrowModal hide={currentIndex <= 0} onClick={showPrevSlide} />
                    <Styled.ModalCloseButton onClickCapture={modalProps.onClose} />
                    <ZoomImage
                        image={allImages[currentIndex]}
                        altTag={allImages[currentIndex].title}
                        onError={modalProps.onClose}
                    />
                    <Styled.RightArrowModal hide={currentIndex >= allImages.length - 1} onClick={showNextSlide} />
                </Styled.ModalContent>
            </Styled.ModalOverlay>
        </Styled.SlideContainer>
    );
}

function ProjectSlider() {
    const projectSliderBlock = useProjectSliderBlock();
    const trackCTAButtonClick = useCTAButtonTracking();
    const { trackEvent } = useOneOffTracking({
        action: 'Content interaction click',
        label: 'Image slider click',
    });

    if (!projectSliderBlock || !projectSliderBlock?.slides?.length) {
        return <Skeleton height="70vh" />;
    }

    const allImages = projectSliderBlock.slides.map(({ image }) => image);

    return (
        <React.Fragment key="project-slider">
            <Styled.SectionContainer id="project-slider" data-testid="project-slider">
                <Center.Vertical>
                    <Styled.Title>{projectSliderBlock.title}</Styled.Title>
                    <Styled.Description>{projectSliderBlock.description}</Styled.Description>
                </Center.Vertical>
                <Carousel
                    onNext={trackEvent}
                    content={projectSliderBlock.slides.map((slide, index) => (
                        <Slide key={slide.id} allImages={allImages} index={index} {...slide} />
                    ))}
                />
                <Styled.ButtonWrapper>
                    <CTAButton
                        data-testid="project-slider-cta-button"
                        onClick={() => trackCTAButtonClick('ga_slider_cta')}
                    >
                        {projectSliderBlock.ctaText}
                    </CTAButton>
                </Styled.ButtonWrapper>
            </Styled.SectionContainer>
            <Divider />
        </React.Fragment>
    );
}

export default ProjectSlider;
